import { forwardRef, useEffect, useRef } from 'react';

import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { IcoCheck } from '@myrealtrip/web-icons';
import { Text, UnstyledButton } from '@myrealtrip/web-ui';

import Show from '~components/Show';
import {
  DEPARTURE_TIME_OPTION_LIST_HEIGHT,
  DEPARTURE_TIME_OPTION_LIST_WIDTH,
} from '~constants/common';
import { DEPARTURE_TIME_LIST } from '~constants/pointToPointTicket';

const OPTION_LIST_PADDING_TOP = 4;

interface Props {
  selectedTime?: string | null;
  onSelectTime: (time: string) => () => void;
}

const TimeOptionList = forwardRef<HTMLUListElement, Props>(
  ({ selectedTime, onSelectTime }, timeOptionRef) => {
    const { colors } = useTheme();
    const optionListRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
      if (optionListRef && optionListRef.current) {
        const selectedElement = optionListRef.current.querySelector(`[data-selected="true"]`);

        if (selectedElement) {
          selectedElement.scrollIntoView({ block: 'start' });
          optionListRef.current.scrollTop -= OPTION_LIST_PADDING_TOP;
        }
      }
    }, []);

    return (
      <Container
        ref={(node) => {
          if (timeOptionRef && typeof timeOptionRef === 'object') {
            (timeOptionRef as React.MutableRefObject<HTMLUListElement | null>).current = node;
          }
          (optionListRef as React.MutableRefObject<HTMLUListElement | null>).current = node;
        }}
      >
        {DEPARTURE_TIME_LIST.map((time) => (
          <TimeItem
            key={time}
            type="button"
            data-selected={selectedTime === time}
            isSelected={selectedTime === time}
            onClick={onSelectTime(time)}
          >
            <TimeText typography={selectedTime === time ? 'paragraphBold15' : 'paragraphNormal15'}>
              {time}
            </TimeText>
            <Show when={selectedTime === time}>
              <IcoCheck size={20} color={colors.blue.s500} />
            </Show>
          </TimeItem>
        ))}
      </Container>
    );
  },
);

TimeOptionList.displayName = 'TimeOptionList';

export default TimeOptionList;

const Container = styled.ul`
  position: relative;
  width: ${DEPARTURE_TIME_OPTION_LIST_WIDTH}px;
  min-height: ${DEPARTURE_TIME_OPTION_LIST_HEIGHT}px;
  overflow-y: auto;

  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const TimeItem = styled(UnstyledButton)<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 9px 12px;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.colors.blue.s80};
      border-radius: 8px;
    `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray.s50};
    border-radius: 8px;
  }
`;

const TimeText = styled(Text)`
  color: ${({ theme }) => theme.colors.gray.s1000};
`;
